import { Box, Button } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import DropBox from '../components/dropAnimation/DropBox';
import '../components/dropAnimation/Dropbox.module.css';
import { useGetUserPetsQuery, UserPet } from '../generated/graphql';
import pets from '../components/sidebar/pets.json';

function pickRandomPet(allPets: any[], ownedPets: UserPet[]): any {
  try {
    const ownedIds = new Set(ownedPets.map((o) => o.petId));

    const remainingByRarity = {
      common: allPets.filter(
        (p) => p.rarity === 'COMMON' && !ownedIds.has(p.id)
      ),
      rare: allPets.filter((p) => p.rarity === 'RARE' && !ownedIds.has(p.id)),
      legendary: allPets.filter(
        (p) => p.rarity === 'LEGENDARY' && !ownedIds.has(p.id)
      ),
    };

    const weights = { common: 0.6, rare: 0.3, legendary: 0.1 };

    const activeRarities = Object.entries(remainingByRarity)
      .filter(([_, list]) => list.length > 0)
      .map(([rarity]) => rarity as keyof typeof weights);

    const total = activeRarities.reduce((sum, r) => sum + weights[r], 0);

    const normalizedOdds = Object.fromEntries(
      activeRarities.map((r) => [r, weights[r] / total])
    ) as Record<'common' | 'rare' | 'legendary', number>;

    const roll = Math.random();
    let acc = 0;

    let chosenRarity: 'common' | 'rare' | 'legendary' = 'common';
    for (const rarity of activeRarities) {
      acc += normalizedOdds[rarity];
      if (roll <= acc) {
        chosenRarity = rarity;
        break;
      }
    }

    const candidates = remainingByRarity[chosenRarity];

    return candidates[Math.floor(Math.random() * candidates.length)];
  } catch (error) {
    console.error('Error picking random pet:', error);
    return null;
  }
}

/**
 * @description Overlay for drop animation
 */
const DropModal = ({
  onClose,
  playerId,
}: {
  onClose: VoidFunction;
  playerId: string;
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [selectedPet, setSelectedPet] = useState<any | null>(null);

  const eventRef = useRef<React.MouseEvent<HTMLElement, MouseEvent> | null>(
    null
  );

  const [{ data: userPetsData }] = useGetUserPetsQuery({
    variables: { playerId },
  });

  const petChosenRef = useRef(false);

  useEffect(() => {
    if (!petChosenRef.current && isOpen && pets && userPetsData) {
      petChosenRef.current = true;

      const allPets = pets;
      const ownedPets = userPetsData.getUserPets;

      const pet = pickRandomPet(allPets, ownedPets);
      setSelectedPet(pet);
    }
  }, [isOpen, userPetsData]);

  useEffect(() => {
    if (showContent) {
      setTimeout(() => {
        setIsOpen(false);
        onClose();
      }, 7000);
    }
  }, [showContent, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
    if (!isOpen) {
      setShowContent(false);
      setShowButtons(false);
    }
  }, [isOpen]);

  if (!isOpen || !selectedPet) return null;

  const disabledBtn = !showButtons || showContent;

  return (
    <Box
      sx={{
        position: 'fixed',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        inset: 0,
        zIndex: 1400,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: showContent ? '125dvh' : '110dvh',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '70%',
          maxWidth: '31.25rem',
          fontSize: '2vw',
        }}
      >
        <DropBox
          pet={selectedPet}
          rarity={selectedPet.rarity}
          shouldOpen={showContent}
          onReady={() => setShowButtons(true)}
          onOpened={() => setShowButtons(false)}
        />

        <Button
          variant="contained"
          disabled={disabledBtn}
          onClick={(e) => {
            eventRef.current = e;
            setShowContent(true);
          }}
          sx={{
            margin: '15px auto',
            display: 'block',
            width: '10rem',
            fontSize: '1.3rem',
            visibility: !showContent ? 'visible' : 'hidden',
          }}
        >
          Open
        </Button>

        <Box
          sx={{
            visibility: !showContent ? 'visible' : 'hidden',
            fontSize: '1.3rem',
            fontWeight: 700,
            textAlign: 'center',
            color: '#fff',
          }}
        >
          Tap “Open” to reveal your new pet!
        </Box>
      </Box>
    </Box>
  );
};

export default DropModal;
